import { useTranslation } from "react-i18next"
import { FilterContainer } from "../components/FilterContainer"
import { SearchBar } from "../components/navbar/SearchBar"
import { ListingListView } from "../components/listing/ListingListView";
import { ListingListViewCarousel } from "../components/listing/ListingListViewCarousel";
import LanguageSelector from "../components/main_page/LanguageSelector"
import { MainMenuButton } from "../components/navbar/MainMenuButton"
import { useAppSelector } from "../store/hooks"
import { PrimaryButton } from "../components/buttons/PrimaryButton";
import { useNavigate } from "react-router-dom";
import logo from '../assets/images/Rentic_logo.svg';
import { NavLink } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { DisplayType } from "../store/features/displaySlice";
import MarkunreadOutlinedIcon from '@mui/icons-material/MarkunreadOutlined';
import { NotificationsButton } from "../components/navbar/NotificationsButton";
import { Categories } from "../components/main_page/Categories";
import { makeTestRequest } from "../api/requests/messages";
import InboxTooltip from "../components/common/InboxNotificationTooltip";
import { InboxNotificationModel, InboxNotificationType } from "../store/features/inboxSlice";
import { notificationsSlice } from "../store/features/notificationsSlice";
import { Helmet } from "react-helmet-async";
import bgElement1 from '../assets/images/backgrounds/rentic_bg_element1.svg';
import bgElement2 from '../assets/images/backgrounds/rentic_bg_element2.svg';
import bgElement3 from '../assets/images/backgrounds/rentic_bg_element3.svg';
import { getBackgroundPublic } from "../api/requests/background";
import { BackgroundPublicModel } from "../api/requests/interfaces/Background";

export interface MainPageProps {
    showSearchBarArea: boolean;
}

export const MainPage: React.FC<MainPageProps> = (props) => {
    const { t } = useTranslation("main");
    const user = useAppSelector(state => state.auth.user);
    const navigate = useNavigate();

    const searchBarRef = useRef<HTMLDivElement | null>(null);
    const isCollidingRef = useRef(false); // Ref to store the latest collision state
    const [searchBarColliding, setSearchBarColliding] = useState(false);

    const [filterOptions, setFilterOptions] = useState<string[] | undefined>();
    const [backgroundConfig, setBackgroundConfig] = useState<BackgroundPublicModel | null>(null);


    const handleScroll = () => {
        const element = searchBarRef.current;

        if (!element) return;

        // Get the bounding rectangle of the element
        const rect = element.getBoundingClientRect();

        if (rect.top <= 64) {
            if (!isCollidingRef.current) {
                isCollidingRef.current = true; // Update the ref
                setSearchBarColliding(true); // Trigger re-render
                console.log("true");
            }
        } else {
            if (isCollidingRef.current) {
                isCollidingRef.current = false; // Update the ref
                setSearchBarColliding(false); // Trigger re-render
                console.log("false");
            }
        }
    };

    useEffect(() => {
        // Fetch background configuration
        const fetchBackground = async () => {
            try {
                const bg = await getBackgroundPublic();
                setBackgroundConfig(bg);
            } catch (error) {
                console.error("Failed to fetch background:", error);
            }
        };
        fetchBackground();

        if (window.innerWidth > 1024) {
            setSearchBarColliding(false);
            return;
        };

        // Add scroll event listener
        window.addEventListener('scroll', handleScroll);

        // Cleanup event listener on component unmount
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };

    }, []);

    const handleBtnClick = async () => {
        const d = await makeTestRequest()
        console.log("D ", d)
    }

    const handleSearch = () => {

    }

    const [metaUpdated, setMetaUpdated] = useState(false);

    useEffect(() => {
        setMetaUpdated(true);
    }, []);

    return (
        <div className="flex flex-col">
            <Helmet>
                <title>Rentic - Rendi Ükskõik Mida</title>
                <meta name="description" content="Otsi või pane asi renti kiirelt ja lihtsalt. Renticus on lai valik erinevaid asju." />
                <meta name="keywords" content="pane asi renti, asjade rent, autorent, laenuta auto, rendifirma, Rentic, rent anything, rendi asju, easy rentals, online rental service, item rental" />
                <meta property="og:title" content="Rentic - Rendi Ükskõik Mida" />
                <meta property="og:description" content="Otsi või pane enda ese renti kiirelt ja lihtsalt. Renticus on lai valik erinevaid asju." />
                <meta property="og:image" content="/logo512.png" />
                <meta property="og:url" content="https://rentic.ee" />
            </Helmet>

            {props.showSearchBarArea ?
            <div className="relative flex flex-col justify-start pt-[100px] lg:pt-0 gap-3 lg:justify-center lg:items-center items-start h-[65vh] overflow-auto bg-gradient-to-br from-blue-50 via-cyan-50 to-blue-100">
                {/* Background image (if configured) */}
                {backgroundConfig?.imageUrl && (
                    <div 
                        className={`absolute inset-0 bg-cover bg-center bg-no-repeat ${backgroundConfig.imageOpacity}`}
                        style={{ backgroundImage: `url(${backgroundConfig.imageUrl})` }}
                    />
                )}
                
                {/* Background decorative elements */}
                {backgroundConfig?.showDecorativeElements && (
                    <div className="absolute inset-0 overflow-hidden pointer-events-none">
                        <img src={bgElement1} alt="" className="absolute top-10 left-10 w-32 h-32 lg:w-48 lg:h-48 opacity-20 animate-float" />
                        <img src={bgElement2} alt="" className="absolute top-20 right-10 w-24 h-24 lg:w-40 lg:h-40 opacity-20 animate-float-delayed" />
                        <img src={bgElement3} alt="" className="absolute bottom-10 left-1/4 w-28 h-28 lg:w-44 lg:h-44 opacity-20 animate-float" />
                    </div>
                )}
                
                {/* White container for search elements */}
                <div className="relative z-10 flex flex-col items-center gap-6 bg-white rounded-3xl shadow-2xl p-8 lg:p-12 mx-4 lg:mx-auto max-w-4xl w-full">
                    <h1 className="text-center text-3xl lg:text-4xl font-bold text-gray-800">Rendi Ükskõik Mida</h1>
                    <div className="w-full h-10" ref={searchBarRef}>
                        <div className={`${!searchBarColliding ? "relative" : "fixed top-[63px] left-4"} z-[9] transition-all w-full`}>
                            <SearchBar />
                        </div>
                    </div>

                    <Categories setFilterOptions={setFilterOptions} />
                </div>
            </div>
            :
            <></>
            }
            <div className="listview_container w-full flex flex-col lg:flex-row  gap-10 justify-start pt-2">
                <div>
                    <FilterContainer searchClick={handleSearch} containerMaxHeight={document.documentElement.clientHeight - 80 - 80} />
                </div>
                <div className="flex flex-col gap-10 w-full pl-1 overflow-hidden">
                    <ListingListView />
                    {/* <ListingListViewCarousel/> */}
                </div>
            </div>
        </div>

    )
}